import type { TransitionV2 } from '../../../ProgramStateMachine.types'
import { v2TransitionSpecificity } from '../../../utils/stateMachineV2'

export interface V2TransitionWarning {
  transitionIndex: number
  overlapsWith: number[]
  equalSpecificity: boolean
  winnerIndex: number | null
  specificity: number
}

const topEventTypes = (t: TransitionV2): string[] =>
  [...new Set(t.when.eventsMatching?.events.map(e => e.type) ?? [])].sort()

const dataEventTypes = (t: TransitionV2): string[] =>
  [...new Set(t.when.data?.eventsMatching?.events.map(e => e.eventType) ?? [])].sort()

const overlap = (a: TransitionV2, b: TransitionV2): boolean => {
  const aTop = topEventTypes(a)
  const bTop = topEventTypes(b)

  if (aTop.length > 0 && bTop.length > 0 && !aTop.some(e => bTop.includes(e))) return false

  const aData = dataEventTypes(a)
  const bData = dataEventTypes(b)

  if (aData.length > 0 && bData.length > 0 && !aData.some(e => bData.includes(e))) return false

  return true
}

export const detectV2OverlapWarnings = (transitions: TransitionV2[]): V2TransitionWarning[] => {
  const specificities = transitions.map(v2TransitionSpecificity)
  const warnings: V2TransitionWarning[] = []

  for (let i = 0; i < transitions.length; i += 1) {
    const overlaps: number[] = []
    let equalSpecificity = false
    let winnerIndex: number | null = null
    let highestPeerSpecificity = -1

    for (let j = 0; j < transitions.length; j += 1) {
      if (i !== j && overlap(transitions[i], transitions[j])) {
        overlaps.push(j)

        if (specificities[i] === specificities[j]) {
          equalSpecificity = true
        }

        if (specificities[j] > specificities[i] && specificities[j] > highestPeerSpecificity) {
          highestPeerSpecificity = specificities[j]
          winnerIndex = j
        }
      }
    }

    if (overlaps.length > 0 && equalSpecificity) {
      const tiedIndexes = overlaps.filter(j => specificities[j] === specificities[i])
      winnerIndex = Math.min(i, ...tiedIndexes)
    }

    if (overlaps.length > 0) {
      warnings.push({
        transitionIndex: i,
        overlapsWith: overlaps,
        equalSpecificity,
        winnerIndex,
        specificity: specificities[i]
      })
    }
  }

  return warnings
}
